import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { formatoDinero } from "../lib/money";
import { useAuth } from "../contexts/AuthContext";
import Modal from "../components/Modal";
import GastoModal from "../components/GastoModal";
import { toast } from "sonner";
import { Plus, AlertTriangle, Ban, Building2, Stethoscope, Wallet, Lock } from "lucide-react";

// Gastos (Control financiero, FASE 2 entrega 2).
//
// 🔴 EL TOTAL NO SE CALCULA AQUÍ: viene calculado del backend, con
//    `SUM(monto) FILTER (WHERE anulado_at IS NULL)`. Esta pantalla no suma dinero ni una
//    vez — si sumara, tendría que acordarse de excluir los anulados, y el día que se
//    olvidara mentiría sobre el gasto del mes.
//
// 🔴 Los importes llegan como CADENA y se pintan con `formatoDinero` (lib/money.js), no con
//    `formatMXN` (DT.236: redondea los centavos y pasa por `Number`).
//
// 🔴 LA DEMO NO VE ESTE MÓDULO, y no se deja que falle con el 403 genérico de
//    `_DEMO_ALLOWED_MUTATIONS`: eso es DT.71, un error que no explica nada. La entrada del
//    menú se oculta (AppLayout.js) y esta pantalla tiene copy propio por si alguien llega
//    escribiendo la URL — dos capas, el patrón de PUENTE-4/5.

const CAMPO = "border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-brand-electric";

export default function Gastos() {
  const { user } = useAuth();
  const esDemo = !!user?.is_demo_session;

  const [datos, setDatos] = useState(null);      // respuesta completa del listado
  const [doctores, setDoctores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);      // {detail, code}
  const [abrirAlta, setAbrirAlta] = useState(false);
  const [anulando, setAnulando] = useState(null); // gasto elegido
  const [motivo, setMotivo] = useState("");
  const [anulandoEnCurso, setAnulandoEnCurso] = useState(false);
  const [errorAnular, setErrorAnular] = useState(null);

  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");
  const [fCategoria, setFCategoria] = useState("");
  const [fDoctor, setFDoctor] = useState("");

  // Fecha de negocio de la clínica, capturada UNA vez de la primera carga sin filtros.
  // 🔴 No se usa `new Date()` del navegador: la fecha de negocio la decide la zona de la
  //    clínica, y el navegador puede estar en otra. Es el mismo motivo por el que el
  //    backend no usa CURRENT_DATE.
  const hoyRef = useRef(null);
  const [hoy, setHoy] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const p = new URLSearchParams();
      if (fDesde) p.set("desde", fDesde);
      if (fHasta) p.set("hasta", fHasta);
      if (fCategoria) p.set("categoria", fCategoria);
      if (fDoctor) p.set("doctor_id", fDoctor);
      const qs = p.toString();
      const { data } = await api.get(`/gastos${qs ? `?${qs}` : ""}`);
      setDatos(data);
      if (hoyRef.current === null && !fDesde && !fHasta) {
        hoyRef.current = data.hasta;
        setHoy(data.hasta);
      }
    } catch (e) {
      // Nunca un catch mudo.
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setCargando(false);
    }
  }, [fDesde, fHasta, fCategoria, fDoctor]);

  useEffect(() => {
    if (esDemo) { setCargando(false); return; }
    cargar();
  }, [cargar, esDemo]);

  useEffect(() => {
    if (esDemo) return;
    api.get("/doctors")
      .then(({ data }) => setDoctores(data?.items || []))
      .catch(() => setDoctores([]));   // el filtro por médico es opcional; sin lista, no se ofrece
  }, [esDemo]);

  async function confirmarAnulacion() {
    const m = motivo.trim();
    if (!m) {
      setErrorAnular({ detail: "Escribe por qué se anula este gasto.", code: "MOTIVO_REQUERIDO" });
      return;
    }
    setAnulandoEnCurso(true);
    setErrorAnular(null);
    try {
      await api.post(`/gastos/${anulando.id}/anular`, { motivo: m });
      toast.success("Gasto anulado");
      setAnulando(null);
      setMotivo("");
      await cargar();
    } catch (e) {
      const code = e?.response?.data?.code || null;
      setErrorAnular({ detail: formatApiError(e?.response?.data?.detail), code });
      if (code === "YA_ANULADO") await cargar();   // la pantalla estaba desactualizada
    } finally {
      // También en el camino de excepción.
      setAnulandoEnCurso(false);
    }
  }

  // ── Demo: copy propio, nunca el 403 genérico ───────────────────────────────
  if (esDemo) {
    return (
      <div className="p-6 max-w-2xl">
        <h1 className="font-display font-bold text-2xl mb-1">Gastos</h1>
        <div className="mt-6 p-6 rounded-xl border border-slate-200 bg-white" data-testid="gastos-demo">
          <Lock className="w-6 h-6 text-slate-400 mb-3" />
          <div className="font-semibold mb-1">El control de gastos no está disponible en la demo</div>
          <p className="text-sm text-slate-600">
            Esta sección registra dinero real de una clínica, así que la demo pública no la incluye.
            En una cuenta real se usa para capturar los gastos del consultorio y los de cada médico,
            y es la base del resultado por médico.
          </p>
        </div>
      </div>
    );
  }

  const items = datos?.items || [];
  const categorias = datos?.categorias || [];

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h1 className="font-display font-bold text-2xl">Gastos</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Lo que sale de la clínica. Con médico, se le imputa a ese médico; sin médico, es del consultorio.
          </p>
        </div>
        <button onClick={() => setAbrirAlta(true)} data-testid="gasto-nuevo"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-brand-electric text-white">
          <Plus className="w-4 h-4" /> Registrar gasto
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-5 p-4 rounded-xl bg-white border border-slate-200">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
          <input type="date" className={CAMPO} value={fDesde} onChange={(e) => setFDesde(e.target.value)}
                 data-testid="filtro-desde" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
          <input type="date" className={CAMPO} value={fHasta} onChange={(e) => setFHasta(e.target.value)}
                 data-testid="filtro-hasta" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Categoría</label>
          <select className={CAMPO} value={fCategoria} onChange={(e) => setFCategoria(e.target.value)}
                  data-testid="filtro-categoria">
            <option value="">Todas</option>
            {categorias.map((c) => <option key={c.valor} value={c.valor}>{c.etiqueta}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Médico</label>
          <select className={CAMPO} value={fDoctor} onChange={(e) => setFDoctor(e.target.value)}
                  data-testid="filtro-doctor">
            <option value="">Todos</option>
            {doctores.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        {(fDesde || fHasta || fCategoria || fDoctor) && (
          <button onClick={() => { setFDesde(""); setFHasta(""); setFCategoria(""); setFDoctor(""); }}
                  className="px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
            Limpiar filtros
          </button>
        )}
      </div>

      {error && (
        <div className="flex gap-2 p-3 mb-4 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800"
             data-testid="gastos-error">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div>{error.detail}</div>
            <button onClick={cargar} className="mt-1 underline font-semibold">Reintentar</button>
          </div>
        </div>
      )}

      {/* Total — calculado en el backend, no aquí */}
      {datos && (
        <div className="flex flex-wrap gap-6 items-end mb-5 p-4 rounded-xl bg-white border border-slate-200">
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Total del periodo</div>
            <div className="font-display font-bold text-2xl mt-0.5" data-testid="gastos-total">
              {formatoDinero(datos.total)}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {datos.count} {datos.count === 1 ? "gasto" : "gastos"} · {datos.desde} → {datos.hasta}
            </div>
          </div>
          {datos.total_anulado && datos.total_anulado !== "0.00" && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Anulados</div>
              <div className="font-display font-semibold text-lg mt-0.5 text-slate-400 line-through"
                   data-testid="gastos-total-anulado">
                {formatoDinero(datos.total_anulado)}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">No suman al total</div>
            </div>
          )}
        </div>
      )}

      {/* Lista */}
      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
        {cargando ? (
          <div className="p-8 text-center text-sm text-slate-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center" data-testid="gastos-vacio">
            <Wallet className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="font-semibold text-slate-700">No hay gastos en este periodo</div>
            <p className="text-sm text-slate-500 mt-1">Registra el primero con el botón de arriba.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="gastos-tabla">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Fecha</th>
                <th className="text-left font-semibold px-4 py-2.5">Categoría</th>
                <th className="text-left font-semibold px-4 py-2.5">De</th>
                <th className="text-left font-semibold px-4 py-2.5">Nota</th>
                <th className="text-right font-semibold px-4 py-2.5">Monto</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => {
                const anulado = !!g.anulado_at;
                return (
                  <tr key={g.id} data-testid={`gasto-fila-${g.id}`}
                      className={`border-t border-slate-100 ${anulado ? "bg-slate-50/60 text-slate-400" : ""}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">{g.fecha}</td>
                    <td className="px-4 py-2.5">{g.categoria_etiqueta}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5">
                        {g.doctor_id
                          ? <><Stethoscope className="w-3.5 h-3.5 shrink-0" />{g.doctor_nombre || "Médico"}</>
                          : <><Building2 className="w-3.5 h-3.5 shrink-0" />Clínica</>}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 max-w-[22rem]">
                      <div className="truncate">{g.nota || <span className="text-slate-400">—</span>}</div>
                      {anulado && (
                        // El anulado se ve, marcado y con su motivo: si desapareciera, nadie
                        // sabría que existió ni por qué se retiró.
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-rose-600">
                          <Ban className="w-3 h-3 shrink-0" />
                          Anulado: {g.anulado_motivo}
                        </div>
                      )}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${anulado ? "line-through" : ""}`}>
                      {formatoDinero(g.monto)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {!anulado && (
                        <button onClick={() => { setAnulando(g); setMotivo(""); setErrorAnular(null); }}
                                data-testid={`gasto-anular-${g.id}`}
                                className="px-2.5 py-1 rounded-lg text-xs text-slate-600 hover:bg-slate-100">
                          Anular
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <GastoModal
        open={abrirAlta}
        onClose={() => setAbrirAlta(false)}
        categorias={categorias}
        doctores={doctores}
        hoy={hoy}
        onCreado={() => cargar()}
      />

      <Modal
        open={!!anulando}
        onClose={anulandoEnCurso ? () => {} : () => setAnulando(null)}
        title="Anular gasto"
        testid="gasto-anular-modal"
        footer={
          <>
            <button onClick={() => setAnulando(null)} disabled={anulandoEnCurso}
                    className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50">
              Cancelar
            </button>
            <button onClick={confirmarAnulacion} disabled={anulandoEnCurso || !motivo.trim()}
                    data-testid="gasto-anular-confirmar"
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 text-white disabled:opacity-50">
              {anulandoEnCurso ? "Anulando…" : "Anular gasto"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {anulando && (
            <div className="text-sm text-slate-600">
              {anulando.categoria_etiqueta} del {anulando.fecha} ·{" "}
              <strong className="text-slate-900">{formatoDinero(anulando.monto)}</strong>
            </div>
          )}
          {errorAnular && (
            <div className="flex gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800"
                 data-testid="gasto-anular-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{errorAnular.detail}</span>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">¿Por qué se anula?</label>
            <input className={`${CAMPO} w-full`} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                   placeholder="Ticket duplicado, importe equivocado…" autoFocus
                   data-testid="gasto-anular-motivo" />
          </div>
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
            El gasto no se borra: queda a la vista, marcado como anulado y con este motivo, y deja de
            sumar al total. Si lo que hace falta es corregir el monto, la fecha, la categoría o el
            médico, anúlalo y regístralo de nuevo — esos datos no se editan.
          </div>
        </div>
      </Modal>
    </div>
  );
}
